import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

interface DropdownMenuProps {
  children: React.ReactNode;
}

interface DropdownContextValue {
  open: boolean;
  rect: DOMRect | null;
  openMenu: (rect: DOMRect) => void;
  close: () => void;
  registerTrigger: (node: HTMLButtonElement | null) => void;
  isInTrigger: (node: Node) => boolean;
}

const DropdownContext = React.createContext<DropdownContextValue>({
  open: false,
  rect: null,
  openMenu: () => {},
  close: () => {},
  registerTrigger: () => {},
  isInTrigger: () => false,
});

function DropdownMenu({ children }: DropdownMenuProps) {
  const [open, setOpen] = React.useState(false);
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  // Triggers store themselves here via a ref callback, which keeps reads
  // off the render path (the lint rule flags `ref.current` during render).
  const triggerNodeRef = React.useRef<HTMLButtonElement | null>(null);

  const openMenu = React.useCallback((r: DOMRect) => {
    setRect(r);
    setOpen(true);
  }, []);

  const close = React.useCallback(() => setOpen(false), []);

  const registerTrigger = React.useCallback((node: HTMLButtonElement | null) => {
    triggerNodeRef.current = node;
  }, []);

  const isInTrigger = React.useCallback((node: Node) => {
    const t = triggerNodeRef.current;
    return !!t && t.contains(node);
  }, []);

  const value = React.useMemo<DropdownContextValue>(
    () => ({ open, rect, openMenu, close, registerTrigger, isInTrigger }),
    [open, rect, openMenu, close, registerTrigger, isInTrigger],
  );

  return (
    <DropdownContext.Provider value={value}>
      <div className="relative inline-block">{children}</div>
    </DropdownContext.Provider>
  );
}

function DropdownMenuTrigger({
  children,
  className,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { open, openMenu, close, registerTrigger } = React.useContext(DropdownContext);

  return (
    <button
      ref={registerTrigger}
      className={className}
      onClick={(e) => {
        onClick?.(e);
        if (open) {
          close();
        } else {
          openMenu(e.currentTarget.getBoundingClientRect());
        }
      }}
      {...props}
    >
      {children}
    </button>
  );
}

function DropdownMenuContent({
  children,
  className,
  align = "end",
}: {
  children: React.ReactNode;
  className?: string;
  align?: "start" | "end";
}) {
  const { open, rect, close, isInTrigger } = React.useContext(DropdownContext);
  const [contentNode, setContentNode] = React.useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (contentNode && !contentNode.contains(target) && !isInTrigger(target)) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close, contentNode, isInTrigger]);

  if (!open || !rect) return null;

  const style: React.CSSProperties = {
    position: "fixed",
    top: rect.bottom + 4,
    ...(align === "end" ? { right: window.innerWidth - rect.right } : { left: rect.left }),
    zIndex: 50,
  };

  return createPortal(
    <div ref={setContentNode} style={style}>
      <div
        className={cn(
          "min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

function DropdownMenuItem({
  children,
  className,
  onClick,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { onClick?: () => void }) {
  const { close } = React.useContext(DropdownContext);

  return (
    <div
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground",
        className,
      )}
      onClick={() => {
        onClick?.();
        close();
      }}
      {...props}
    >
      {children}
    </div>
  );
}

function DropdownMenuSeparator({ className }: { className?: string }) {
  return <div className={cn("-mx-1 my-1 h-px bg-muted", className)} />;
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
};
